import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { useEnum } from "@/api/services/global/enum";
import { createOrUpdateRenewVisaBasicInformationDocumentGroupApi } from "@/api/services/main/renew-visa";
import { TGetRenewVisaBasicInformationDocumentGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-basic-information-document-group.dto";
import { Button } from "@/components/elements/Button";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { RenewVisaStepCard } from "@/features/renew-visa/components/RenewVisaStepCard";
import { renewVisaBasicInfoSchema } from "@/features/renew-visa/schema/renewVisaBasicInfoSchema";
import { TRenewVisaBasicInfoForm } from "@/features/renew-visa/types/renewVisaBasicInfoForm";
import { text } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

export type TBasicInfoStepCardProps = {
  isSkipStep: boolean;
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  basicInfoData?: TGetRenewVisaBasicInformationDocumentGroupOutputDto;
  readonly: boolean;
};

export const BasicInfoStepCard: FC<TBasicInfoStepCardProps> = ({ stepGroupData, isSkipStep, basicInfoData, readonly }) => {
  const { stepOrder, stepName } = stepGroupData.masterDocumentStep;
  const { id: renewVisaDocumentStepGroupId } = stepGroupData;
  const { employeeRenewVisaDocumentGroupId } = useParams();
  const queryClient = useQueryClient();

  const { data: renewVisaConditionOptions } = useEnum("ERenewVisaCondition");

  const defaultConditions = useMemo(() => {
    const arr = basicInfoData ? basicInfoData.conditions : [];
    return arr.length === 0 ? ["NONE_A_TO_E"] : arr;
  }, [basicInfoData]);

  const methods = useForm<TRenewVisaBasicInfoForm>({
    defaultValues: {
      conditions: defaultConditions,
    },
  });

  const { control } = methods;

  const conditionWatchField = useWatch({ control, name: "conditions" });

  const onSubmitRenewVisaBasicInfo = async (data: TRenewVisaBasicInfoForm) => {
    if (!employeeRenewVisaDocumentGroupId || !renewVisaDocumentStepGroupId) return;
    try {
      const rs = await createOrUpdateRenewVisaBasicInformationDocumentGroupApi(employeeRenewVisaDocumentGroupId, renewVisaDocumentStepGroupId, {
        conditions: data.conditions.filter((item) => item !== "NONE_A_TO_E"),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_BASIC_INFORMATION_GROUP] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_DOCUMENT_CREATION_GROUP] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_UPLOAD_FILE] });
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmitRenewVisaBasicInfo} schema={renewVisaBasicInfoSchema}>
      <RenewVisaStepCard
        isSkip={isSkipStep}
        isCompleted={stepGroupData.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
        title={`ステップ${stepOrder}`}
        subTitle={stepName}
        buttons={
          <Button variant="outline" size="sm" type="submit" disabled={readonly || conditionWatchField.length === 0 || isSkipStep}>
            条件を更新
          </Button>
        }
      >
        <Typography variant="body14Bold" color={text.secondary}>
          基本情報を確認すると今回必要な添付書類が「ステップ2 申請データの作成」に表示されます。以下に
          <Typography variant="body14Bold" color={text.accent}>
            該当する場合のみ
          </Typography>
          チェックを入れてください。
        </Typography>
        <Stack mt={2} gap="8px">
          <FormField control={control} name="conditions">
            <CheckboxGroup
              options={[
                ...renewVisaConditionOptions,
                {
                  label: "AからEのいずれも該当しない。",
                  value: "NONE_A_TO_E",
                },
              ]}
              disabled={readonly || isSkipStep}
              onChange={(value) => {
                if (value.slice(-1).toString() === "NONE_A_TO_E") {
                  methods.setValue("conditions", ["NONE_A_TO_E"]);
                } else {
                  methods.setValue(
                    "conditions",
                    value.filter((item) => item !== "NONE_A_TO_E").map((item) => item.toString()),
                  );
                }
              }}
            />
          </FormField>
        </Stack>
      </RenewVisaStepCard>
    </Form>
  );
};
